import { useState, useMemo, useEffect } from 'react';
import { useCabinet } from '../useCabinet';
import { getScoped, setScoped } from '../../../lib/storage';
import SectionTitle from '../SectionTitle';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function formatWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  return `${formatDate(monday)} – ${formatDate(sunday)}`;
}

interface DayShift {
  enabled: boolean;
  startHour: number;
  durationHours: number;
}

function calcPay(startHour: number, hours: number, dayRate: number, nightRate: number) {
  let dayHours = 0;
  let nightHours = 0;
  for (let i = 0; i < hours; i++) {
    const h = (startHour + i) % 24;
    if (h >= 6 && h < 22) {
      dayHours++;
    } else {
      nightHours++;
    }
  }
  return { dayHours, nightHours, total: dayHours * dayRate + nightHours * nightRate };
}

const startHourOptions = Array.from({ length: 24 }, (_, i) => i);

export default function Schedule() {
  const { config, scope } = useCabinet();
  const { day: dayRate, night: nightRate } = config.rates;

  const today = new Date();
  const thisMonday = getMonday(today);
  const nextMonday = addDays(thisMonday, 7);

  const [weekOffset, setWeekOffset] = useState<number>(() => {
    const saved = getScoped(scope, 'schedule_week');
    const parsed = saved ? parseInt(saved, 10) : 1;
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  });
  const [shifts, setShifts] = useState<Record<number, DayShift>>(() => {
    try {
      const saved = getScoped(scope, 'schedule_shifts');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  useEffect(() => {
    setScoped(scope, 'schedule_week', String(weekOffset));
  }, [scope, weekOffset]);

  useEffect(() => {
    setScoped(scope, 'schedule_shifts', JSON.stringify(shifts));
  }, [scope, shifts]);

  const currentMonday = useMemo(() => addDays(nextMonday, (weekOffset - 1) * 7), [weekOffset, nextMonday]);

  const weekLabel = useMemo(() => formatWeekRange(currentMonday), [currentMonday]);

  const totalPay = useMemo(() => {
    return Object.values(shifts).reduce((acc, s) => {
      if (!s.enabled) return acc;
      const { total } = calcPay(s.startHour, s.durationHours, dayRate, nightRate);
      return acc + total;
    }, 0);
  }, [shifts, dayRate, nightRate]);

  const totalHours = useMemo(() => {
    return Object.values(shifts).reduce((acc, s) => {
      if (!s.enabled) return acc;
      return acc + s.durationHours;
    }, 0);
  }, [shifts]);

  function toggleDay(dayIndex: number) {
    setShifts((prev) => {
      const current = prev[dayIndex];
      if (current?.enabled) {
        return { ...prev, [dayIndex]: { ...current, enabled: false } };
      }
      return { ...prev, [dayIndex]: { enabled: true, startHour: 9, durationHours: 8 } };
    });
  }

  function updateShift(dayIndex: number, key: 'startHour' | 'durationHours', value: number) {
    setShifts((prev) => ({
      ...prev,
      [dayIndex]: { ...(prev[dayIndex] || { enabled: true, startHour: 9, durationHours: 8 }), [key]: value },
    }));
  }

  function handleSave() {
    setShowBlockedModal(true);
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <SectionTitle accent="Расписание" />
        <p className="text-foreground-500 text-sm">Выберите рабочие смены на предстоящие недели</p>
      </div>

      {/* Info bar */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-2 bg-surface-1 border border-surface-3 rounded-xl px-4 py-2.5 text-sm">
          <span className="w-3 h-3 rounded-full bg-accent-200 flex-shrink-0" />
          <span className="text-foreground-600">Дневная ставка <strong className="text-ink">380₽</strong>/час (06:00–22:00 МСК)</span>
        </div>
        <div className="flex items-center gap-2 bg-surface-1 border border-surface-3 rounded-xl px-4 py-2.5 text-sm">
          <span className="w-3 h-3 rounded-full bg-night flex-shrink-0" />
          <span className="text-foreground-600">Ночная ставка <strong className="text-ink">420₽</strong>/час (22:00–06:00 МСК)</span>
        </div>
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => setWeekOffset((w) => Math.max(1, w - 1))}
          disabled={weekOffset <= 1}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-surface-5 text-foreground-600 hover:text-ink hover:border-surface-6 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-2-line text-primary-300" />
          </span>
          {weekLabel}
        </div>
        <button
          onClick={() => setWeekOffset((w) => w + 1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-surface-5 text-foreground-600 hover:text-ink hover:border-surface-6 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {/* Days grid */}
      <div className="space-y-3 mb-6">
        {DAYS.map((dayName, idx) => {
          const dateOfDay = addDays(currentMonday, idx);
          const shift = shifts[idx];
          const isEnabled = !!shift?.enabled;
          const pay = isEnabled ? calcPay(shift.startHour, shift.durationHours, dayRate, nightRate) : null;

          return (
            <div
              key={idx}
              className={`bg-surface-1 border rounded-2xl p-5 transition-all duration-200 ${
                isEnabled ? 'border-primary-500/30' : 'border-surface-3'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Day toggle */}
                <div className="flex items-center gap-3 min-w-[120px]">
                  <button
                    onClick={() => toggleDay(idx)}
                    className={`w-10 h-6 rounded-full transition-all duration-200 flex-shrink-0 cursor-pointer relative ${
                      isEnabled ? 'bg-primary-600' : 'bg-surface-5'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-200 ${
                        isEnabled ? 'left-5' : 'left-1'
                      }`}
                    />
                  </button>
                  <div>
                    <p className="text-sm font-semibold text-ink">{dayName}</p>
                    <p className="text-xs text-foreground-400">{formatDate(dateOfDay)}</p>
                  </div>
                </div>

                {isEnabled && (
                  <>
                    {/* Start time */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-foreground-400">Начало</label>
                      <select
                        value={shift.startHour}
                        onChange={(e) => updateShift(idx, 'startHour', Number(e.target.value))}
                        className="bg-surface-2 border border-surface-5 rounded-xl px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-500 cursor-pointer"
                      >
                        {startHourOptions.map((h) => (
                          <option key={h} value={h}>
                            {String(h).padStart(2, '0')}:00
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Duration */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-foreground-400">Длительность</label>
                      <div className="flex gap-1.5">
                        {[2, 4, 6, 8, 10].map((h) => (
                          <button
                            key={h}
                            onClick={() => updateShift(idx, 'durationHours', h)}
                            className={`w-9 h-9 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                              shift.durationHours === h
                                ? 'bg-primary-600 text-on-primary'
                                : 'bg-surface-2 border border-surface-5 text-foreground-600 hover:border-primary-500/50 hover:text-ink'
                            }`}
                          >
                            {h}ч
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Pay summary */}
                    {pay && (
                      <div className="ml-auto text-right">
                        <p className="text-lg font-bold text-primary-300">
                          {pay.total.toLocaleString('ru-RU')}₽
                        </p>
                        <p className="text-xs text-foreground-400">
                          {pay.dayHours > 0 && `${pay.dayHours}ч дн.`}
                          {pay.dayHours > 0 && pay.nightHours > 0 && ' + '}
                          {pay.nightHours > 0 && `${pay.nightHours}ч ноч.`}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {!isEnabled && (
                  <p className="text-sm text-foreground-300 ml-auto">Выходной</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary & save */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-surface-1 border border-surface-3 rounded-2xl px-6 py-5">
        <div className="flex gap-8">
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Итого часов</p>
            <p className="text-lg font-bold text-ink">{totalHours} ч</p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Прогноз выплаты</p>
            <p className="text-lg font-bold text-primary-300">
              {totalPay.toLocaleString('ru-RU')} ₽
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          className="bg-primary-600 hover:bg-primary-600 text-on-primary font-semibold rounded-xl px-8 py-3 text-sm transition-all cursor-pointer whitespace-nowrap"
        >
          Сохранить расписание
        </button>
      </div>

      {/* Blocked modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-1 border border-surface-5 rounded-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-500/10 mx-auto mb-5">
              <i className="ri-error-warning-line text-3xl text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-red-400 mb-3">Внимание</h3>
            <p className="text-sm text-red-400 leading-relaxed mb-6">
              Вы можете выставлять свои смены только после полного оформления
            </p>
            <button
              onClick={() => setShowBlockedModal(false)}
              className="w-full bg-surface-2 hover:bg-surface-4 border border-surface-5 text-foreground-700 rounded-xl py-3 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Понятно
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
